import React, { useEffect, useState } from "react";
import SuperUserHeader from "../../components/SuperUserHeader";
import SuperUserSideMenu from "../../components/SuperUserSideMenu";
import { useNavigate, useParams } from "react-router-dom";
import TextInput from "../../components/TextInput";
import ConfirmPopup from "../../components/ConfirmPopup";
import CustomButton from "../../components/CustomButton";
import ReturnButton from "../../components/ReturnButton";
import { ThunkDispatch } from "@reduxjs/toolkit";
import { useSelector, useDispatch } from "react-redux";
import { useSnackbar } from "notistack";
import CustomToast from "../../components/CustomToast";
import {
    setSuccess,
    setError,
    getCustomerCompanyDetail,
    getDepartmentOfCompany,
    updateDepartmentOfCompany,
    createDepartmentOfCompany,
    deleteDepartmentOfCompany,
} from "../../reducers/customerCompany";
import NotFoundPage from "../NotFoundPage";

const initialData = {
    department: "",    
};

const initialErrorData = {
    department: "",
};

const initTableHeaders = [
    {
        title: "所属名",
        customStyle: "w-[280px]"
    },
    {
        title: "",
        customStyle: ""
    },
]

const DepartmentRegistration = () => {
    const navigate = useNavigate();
    const dispatch = useDispatch<ThunkDispatch<any, any, any>>();
    const [formData, setFormData] = useState(initialData);
    const [errors, setErrors] = useState(initialErrorData);
    const [tableHeaders, setTableHeaders] = useState(initTableHeaders);
    const [departmentList, setDepartmentList] = useState([]);
    const [isDisabled, setIsDisabled] = useState(true);
    const [open, setOpen] = useState(false);
    const [confirmText, setConfirmText] = useState<any>(null);
    const [deleteId , setDeleteId ] = useState(null);
    const {
        loading,
        success,
        error,
        actionName,
        detailData,
        initDepartmentList,
        isReloadDepartment,
        passCheckPage,
    } = useSelector((state: any) => state.customerCompany);
    const { id } = useParams<{ id: string }>();
            
    useEffect(() => {
        if(id){
            dispatch(getCustomerCompanyDetail({id: Number(id)}));
            dispatch(getDepartmentOfCompany({companyId: Number(id)}));
        }
    }, [id, isReloadDepartment]);

    useEffect(() => {
        if(initDepartmentList){
            setDepartmentList(initDepartmentList);
        }
    }, [initDepartmentList]);

    const validateFormData = () => {
        let formErrors = {
            department: "",
        };

        if (!formData.department.trim()) {
            formErrors.department = "所属名は必須です。";
        }
        
        setErrors(formErrors);
        return !Object.values(formErrors).some((error) => error);
    };

    const handleSubmit = (e: any) => {
        e.preventDefault();
        if (validateFormData()) {
            const data = {
                requestBody: {
                    name: formData.department.trim(),
                    company_id: Number(id),
                }
            }
            dispatch(createDepartmentOfCompany(data));
            setFormData(initialData);
            setIsDisabled(true);
        }
    };

    const handleChange = (e: any) => {
        const { name, value } = e.target;
        setFormData((prev) => ({
            ...prev,
            [name]: value,
        }));
        
        if(value){
            setIsDisabled(false);
        }
        else{
            setIsDisabled(true);
        }
    };

    const handleDelete = (item: any) => {
        setDeleteId(item.id);
        setConfirmText(
            <>
                <p>
                    {`${item.name}を削除します。よろしいですか？`}
                </p>
            </>
        );
        setOpen(true);
    }

    const handleConfirm = () => {
        if(deleteId) dispatch(deleteDepartmentOfCompany({id: Number(deleteId)}));
        setDeleteId(null);
        setOpen(false);
    }

    const handleClick = (id: any) => {
        const updatedDepartmentList = initDepartmentList.map((item: any) => item.id === id ? { ...item, isActive: true } : { ...item, isActive: false }) as any;
        setDepartmentList(updatedDepartmentList);
    }

    const setInActive = () => {
        setDepartmentList(initDepartmentList);
    };

    useEffect(() => {
        const handleClickOutside = () => {
            setInActive();
        };

        document.addEventListener("click", handleClickOutside);

        return () => {
            document.removeEventListener("click", handleClickOutside);
        };
    }, [departmentList]);

    const handleCancelUpdate = () => {
        setInActive();
    }

    const handleChangeField = (e: any, id: any) => {
        const { name, value } = e.target;
        const updatedDepartmentList = departmentList.map((item: any) => item.id === id ? { ...item, name: value } : { ...item }) as any;
        setDepartmentList(updatedDepartmentList);
    }

    const handleUpdate = (department: any) => {
        if(!department.name) {
            const updatedDepartmentList = departmentList.map((item: any) => item.id === department.id ? { ...item, error: "所属名は必須です。" } : { ...item }) as any 
            setDepartmentList(updatedDepartmentList);
            return;
        }

        const data = {
            id: Number(department.id),
            requestBody: {
                name: department.name,
                company_id: Number(id),
            }
        }
        dispatch(updateDepartmentOfCompany(data));
    }

    const { enqueueSnackbar, closeSnackbar } = useSnackbar();
    useEffect(() => {
        if(error && passCheckPage === "passed") {
            enqueueSnackbar("I love snacks.", {
                content: (key) => (
                <CustomToast
                    title={"メッセージ"}
                    message={error}
                    variant={"error"}
                    snackbarKey={key}
                    closeSnackbar={closeSnackbar}
                />
                ),
            });
            dispatch(setError(null));
        }
        else if(success && passCheckPage === "passed") {
            enqueueSnackbar("I love snacks.", {
                content: (key) => (
                <CustomToast
                    title={"メッセージ"}
                    message={success}
                    variant={"success"}
                    snackbarKey={key}
                    closeSnackbar={closeSnackbar}
                />
                ),
            });
            dispatch(setSuccess(null));
        }
    }, [error, success]);

    if (passCheckPage && passCheckPage !== "passed") {
        if(passCheckPage === "failed") {
            dispatch(setError(null));
            return <NotFoundPage />
        }
        return null;
    }
 
    return (
        <div className="bg-alu-base h-dvh">
        <SuperUserHeader />
        <div className="mt-6 px-6 flex gap-6 relative">
            <SuperUserSideMenu />
            <div className="flex-1">
                <div className="flex items-center justify-between">
                    <div className="mb-6 flex items-center gap-4">
                        <ReturnButton onClick={() => navigate("/control/company/detail/" + detailData.id)} />
                        <span className="text-sm alu-text-base-1">/</span>
                        <h3 className="text-xl leading-[1.875rem] alu-text-base-2 font-bold">
                            {detailData.name + " の所属"}
                        </h3>
                    </div>
                    <div className="mb-6">
                        <CustomButton
                            type="button"
                            variant="text"
                            onClick={() => navigate("/control/position/register/" + detailData.id)}
                        >
                            → 役職の登録
                        </CustomButton>
                    </div>
                </div>
                <div className="bg-white rounded-lg mb-6">
                    <div className="flex gap-2 p-4 border-b alu-border">
                        <div className="w-[272px]">
                            <TextInput
                                name="department"
                                placeholder="所属名を入力"
                                value={formData.department}
                                onChange={handleChange}
                                error={errors.department}
                                errorText={errors.department}
                                required
                                size="md"
                                customClassName="outline-none"
                            />
                        </div>
                        <div className="block">
                            <CustomButton
                                type="button"
                                customStyle="!py-2 !px-4 w-[3.75rem] h-9"
                                onClick={(e) => {
                                    e.stopPropagation();
                                    handleSubmit(e);
                                }}
                                disabled={isDisabled}
                            >
                                追加
                            </CustomButton>
                        </div>
                    </div>
                    <table className="table-fixed w-full">
                        <thead className="border-b alu-border px-4 py-2">
                            <tr>
                            {tableHeaders.map((header, index) => (
                                <th
                                key={index}
                                className={`px-4 py-3 text-left ${
                                    header.customStyle ? header.customStyle : ""}`}
                                >
                                    <p className="font-normal alu-text-base-1 text-sm leading-[1.3125]">{header.title}</p>                                    
                                </th>
                            ))}
                            </tr>
                        </thead>
                        <tbody>
                            {departmentList.map((item: any, idx: number) => (
                            <tr
                                className="border-b alu-border"
                                key={idx}
                            >
                                <td className="px-4 py-2 text-left font-normal text-sm leading-[1.3125] alu-text-base-2">
                                    <TextInput
                                        name={"department" + item.id}
                                        placeholder="所属名を入力"
                                        value={item.name}
                                        onChange={(e: any) => handleChangeField(e, item.id)}
                                        error={item.error}
                                        errorText={item.error}
                                        required
                                        size="md"
                                        customClassName={`${!item.error ? "border-transparent outline-none hover:border-solid hover:border-[#FF861A] active:border-[#FF861A]" : ""}`}
                                        onClick={(e: any) => {
                                            e.stopPropagation();
                                            handleClick(item.id);
                                        }}
                                    />
                                </td>
                                <td>
                                    {!item.isActive ?
                                    <p
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            handleDelete(item);
                                        }}
                                        className="inline-block hover:cursor-pointer min-w-[80px] px-4 py-4 pl-5 text-left font-normal text-sm leading-[1.3125] alu-text-title"
                                    >削除...</p>
                                    :
                                    <div className="flex items-center gap-2">
                                        <CustomButton
                                            type="button"
                                            size="md"
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                handleUpdate(item);
                                            }}
                                        >
                                            更新
                                        </CustomButton>
                                        <CustomButton
                                            type="button"
                                            variant="text"
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                handleCancelUpdate();
                                            }}
                                        >
                                            キャンセル
                                        </CustomButton>
                                    </div>
                                    }
                                </td>
                            </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            </div>
        </div>
        {open && confirmText && (
            <ConfirmPopup
                open={open}
                onClose={() => setOpen(false)}
                confirmContent={confirmText}
                onConfirm={(e: any) => {
                    e.stopPropagation();
                    handleConfirm();
                }}
            />
        )}
        </div>
    );
};

export default DepartmentRegistration;
